import { COMMON } from '#app/theme/constants.js';
import { FLEX, LAYOUT } from '#app/theme/index.js';
import { sx } from '#app/theme/utils.js';
import { gradientBorderBackground } from '#app/utilities/styles.js';
import { buttonStyles } from './constants.js';
import {
  type ButtonSize,
  type ButtonStyleStateObject,
  type ButtonStyleStates,
  type ButtonVariant,
  type StyledSystemButtonProps,
} from './types.js';
import { css, styled } from 'styled-components';

const getState = ({
  isDisabled,
  isFocused,
  isHovered,
}: {
  isDisabled: boolean;
  isFocused: boolean;
  isHovered: boolean;
}): ButtonStyleStates => {
  if (isDisabled) return 'disabled';
  if (isFocused) return 'focused';
  if (isHovered) return 'hovered';

  return 'default';
};

const getVariantStateStyles = ({
  isDisabled,
  isFocused,
  isHovered,
  variant,
}: {
  isDisabled: boolean;
  isFocused: boolean;
  isHovered: boolean;
  variant: ButtonVariant;
}): ButtonStyleStateObject => {
  const state = getState({ isDisabled, isFocused, isHovered });

  return buttonStyles[variant][state];
};

const getMinWidth = ({
  removeMinWidth,
  size,
  variant,
}: {
  removeMinWidth: boolean;
  size: ButtonSize;
  variant: ButtonVariant;
}) => {
  if (
    removeMinWidth ||
    variant === 'V2Link' ||
    variant === 'V2DestructiveLink'
  ) {
    return css`
      min-width: unset;
      width: max-content;
    `;
  }

  switch (size) {
    case 'small': {
      return css`
        min-width: 75px;
      `;
    }

    case 'xsmall': {
      return css`
        min-width: 60px;
      `;
    }

    case 'large':
    case 'medium':
    default: {
      return css`
        min-width: 100px;
      `;
    }
  }
};

const getRadiusSize = (size: ButtonSize, variant?: ButtonVariant): number => {
  if (variant === 'V2Link' || variant === 'V2DestructiveLink') {
    return 0;
  }

  switch (size) {
    case 'small':
      return 20;

    case 'xsmall':
      return 16;

    default:
      return 24;
  }
};

/**
 * Returns the correct padding/height based on the size.
 */
const getSize = (size: ButtonSize, variant?: ButtonVariant) => {
  // Medium sizing.
  let height = '48px';
  let padding = '24px';

  // These might need updated once Design commits to some sizes.
  switch (size) {
    case 'small': {
      height = '40px';
      padding = '16px';
      break;
    }

    case 'xsmall': {
      height = '32px';
      padding = '12px';
      break;
    }

    default: {
      break;
    }
  }

  if (variant === 'V2Link' || variant === 'V2DestructiveLink') {
    padding = '0px';
  }

  return css`
    height: ${height};
    padding-left: ${padding};
    padding-right: ${padding};
    border-radius: ${getRadiusSize(size, variant)}px;
  `;
};

const getStyle = ({
  isDisabled,
  isFocused,
  isHovered,
  isLoading,
  size,
  variant,
}: {
  isDisabled: boolean;
  isFocused: boolean;
  isHovered: boolean;
  isLoading: boolean;
  size: ButtonSize;
  variant: ButtonVariant;
}) => {
  const {
    backgroundColor,
    backgroundImage = 'none',
    borderColor = 'transparent',
    borderWidth = '1px',
    color,
    gradientBorderBackgroundOptions = null,
  } = getVariantStateStyles({
    isDisabled: isDisabled || isLoading,
    isFocused,
    isHovered,
    variant,
  });

  return css`
    border-width: ${borderWidth};
    border-style: solid;
    border-color: ${borderColor};
    background-image: ${backgroundImage};
    ${gradientBorderBackgroundOptions &&
    gradientBorderBackground({
      ...gradientBorderBackgroundOptions,
      radius: getRadiusSize(size),
    })};
    background-color: ${backgroundColor};
    color: ${color};
  `;
};

const getCursor = ({
  isDisabled,
  isLoading,
}: {
  isDisabled: boolean;
  isLoading: boolean;
}) => {
  if (isLoading) {
    return css`
      cursor: wait;
    `;
  }

  if (isDisabled) {
    return css`
      cursor: not-allowed;
    `;
  }

  return css`
    cursor: pointer;
  `;
};

export const StyledButton = styled.button<
  StyledSystemButtonProps & {
    $block: boolean;
    $isDisabled: boolean;
    $isFocused: boolean;
    $isHovered: boolean;
    $isLoading: boolean;
    $removeMinWidth: boolean;
    size: ButtonSize;
    variant: ButtonVariant;
    zIndex?: number;
  }
>`
  ${({
    $block,
    $isDisabled,
    $isFocused,
    $isHovered,
    $isLoading,
    $removeMinWidth,
    size,
    variant,
    zIndex,
  }) => css`
    position: relative;
    z-index: ${zIndex === undefined ? 1 : zIndex};
    text-align: center;
    text-transform: none;
    display: ${variant === 'V2Download'
      ? 'flex'
      : $block
        ? 'block'
        : 'inline-block'};
    width: ${$block ? '100%' : 'auto'};
    transition: all 150ms ease-out;
    overflow: hidden;
    cursor: ${$isDisabled || $isLoading ? 'not-allowed' : 'pointer'};

    ${getCursor({ isDisabled: $isDisabled, isLoading: $isLoading })}
    ${getSize(size, variant)}
    ${getMinWidth({ removeMinWidth: $removeMinWidth, size, variant })}
    ${getStyle({
      isDisabled: $isDisabled,
      isFocused: $isFocused,
      isHovered: $isHovered,
      isLoading: $isLoading,
      size,
      variant,
    })}
  `}

  ${COMMON}
  ${FLEX}
  ${LAYOUT}
  ${sx}
`;
